import {
  Home,
  Logout,
  Mail,
  Message,
  Person,
  Settings,
  Storage,
} from '@mui/icons-material';
import { On3IconWordpress } from '@on3/icons';
import { Link } from '@on3/ui-lib/components/Link';
import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import { useAuth, useSite } from '@on3/ui-lib/index';
import { cmsUrl, databaseUrl } from '@on3/ui-lib/utils/api';
import { User } from '@public/components/Svg/User';
import { useMemo, useRef, useState } from 'react';

import styles from './UserMenu.module.scss';

const Menu = () => {
  const { user, hasArticleEditAccess, hasRdbAccess, logout } = useAuth();
  const { siteUrls } = useSite();

  const sites = useMemo(
    () =>
      siteUrls?.filter(
        (site) =>
          (site.url?.includes('/teams/') || site.url?.includes('/sites/')) &&
          user?.si?.includes(site.key),
      ) ?? [],
    [siteUrls, user?.si],
  );

  const hasProfiles = user?.rg?.includes('profile_edit') || !!user?.pks?.length;

  const forums = useMemo(
    () => sites.filter((site) => site.primaryForum) ?? [],
    [sites],
  );

  const settingsUrl = useMemo(() => {
    if (sites.length > 0) {
      return `${sites[0].url}/my/settings/`;
    }

    return '/my/settings/';
  }, [sites]);

  return (
    <nav data-menu-ui="user-menu">
      <ul>
        {sites.length > 0 ? (
          sites.map((site) => (
            <li key={site.key}>
              <Link href={site.url as string}>
                <Home />
                {site.name}
              </Link>
            </li>
          ))
        ) : (
          <li>
            <Link href="/">
              <Home />
              Home
            </Link>
          </li>
        )}
        {forums.map((forum) => (
          <li key={forum.key}>
            <Link href={forum.primaryForum?.url as string}>
              <Message />
              {forum.primaryForum?.title}
            </Link>
          </li>
        ))}
        <li>
          <Link href="/boards/conversations/">
            <Mail />
            Inbox
          </Link>
        </li>
        <li>
          <Link href={settingsUrl}>
            <Settings />
            Settings
          </Link>
        </li>
        {hasProfiles && (
          <li>
            <Link forceReload href="/my/profiles/">
              <Person />
              My Profile
            </Link>
          </li>
        )}
        {hasRdbAccess() && (
          <li>
            <Link href={databaseUrl} rel="noopener noreferrer" target="_blank">
              <Storage />
              Database Admin
            </Link>
          </li>
        )}
        {hasArticleEditAccess() && (
          <li>
            <Link
              href={`${cmsUrl}/wp-admin/`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <On3IconWordpress />
              WordPress Backend
            </Link>
          </li>
        )}
        <li>
          <button onClick={() => logout('/login/')} type="button">
            <Logout />
            Sign Out
          </button>
        </li>
      </ul>
    </nav>
  );
};

export const UserMenu = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  useClickOutside(ref, () => setIsMenuOpen(false));

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  return (
    <div className={styles.block} data-menu-ui="user" ref={ref}>
      <button
        className={styles.button}
        name="user-menu"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        type="button"
      >
        <User />
      </button>
      {isMenuOpen && <Menu />}
    </div>
  );
};
